import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Grid, Step, StepLabel, Stepper } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import useSettings from '../hooks/useSettings';
import useSelection from '../hooks/useSelection';
import { executeApi } from '../Utils';

import TemplateStep from './steps/TemplateStep';
import UploadStep from './steps/UploadStep';
import ResultStep from './steps/ResultStep';

interface ImportablesDialogProps {
	module: string;
	params: any;
	updateRefresh: VoidFunction;
}

export function showImportablesDialog(props: ImportablesDialogProps) {
	return openDialog({
		children: <ImportablesDialog {...props} />,
		fullWidth: true,
		fullScreen: false,
		maxWidth: 'md'
	});
}

export default function ImportablesDialog(props: ImportablesDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const { selectedItems: selectedTemplates } = useSelection({
		mode: 'single',
		module: 'sys_importables_templates',
		isSelecting: true
	});

	const templateUid = selectedTemplates?.[0]?.impt_uid;

	const [fileBlob, setFileBlob] = React.useState<string>();

	const [isProcessing, setIsProcessing] = React.useState(true);
	const [error, setError] = React.useState<any>();

	const [activeStepIndex, setActiveStepIndex] = React.useState(0);

	const previousStep = () => {
		if (fileBlob) setFileBlob(undefined);

		if (activeStepIndex === 0) dispatch(closeDialog());
		else setActiveStepIndex(stepIndex => stepIndex - 1);
	};

	const nextStep = () => {
		if (activeStepIndex === steps.length - 1) dispatch(closeDialog());
		else setActiveStepIndex(stepIndex => stepIndex + 1);
	};

	function importFile() {
		const body = new FormData();
		Object.entries<any>(props.params).forEach(([key, value]) => body.append(key, value));

		if (fileBlob) {
			setIsProcessing(true);
			nextStep();

			body.append('file', fileBlob);

			executeApi.post({
				url: `/api/fileimportables/spreadsheet?templateUid=${templateUid}`,
				body,
				config: { headers: { 'Content-Type': 'multipart/form-data' } },
				dispatch,
				successHandler: response => {
					setIsProcessing(false);
					props.updateRefresh();
				},
				errorHandler: ex => {
					setIsProcessing(false);
					setError(ex);
				}
			});
		}
	}

	const steps = [
		{
			label: t('IMPORTABLES_DIALOG_IMPORT_STEP_TEMPLATE'),
			content: <TemplateStep module={props.module} selectedTemplates={selectedTemplates} />,
			handleNextStep: () => {
				nextStep();
			},
			disableNext: !templateUid
		},
		{
			label: t('IMPORTABLES_DIALOG_IMPORT_STEP_UPLOAD'),
			content: <UploadStep fileBlob={fileBlob} setFileBlob={setFileBlob} />,
			handleNextStep: () => {
				importFile();
			},
			disableNext: !fileBlob
		},
		{
			label: t('IMPORTABLES_DIALOG_IMPORT_STEP_IMPORT'),
			content: <ResultStep isProcessing={isProcessing} error={error} updateRefresh={props.updateRefresh} />
		}
	];

	const activeStep = steps[activeStepIndex];
	const isLastStep = activeStepIndex === steps.length - 1;

	return (
		<>
			<DialogTitle>{t('IMPORTABLES_DIALOG_IMPORT_TITLE')}</DialogTitle>
			<DialogContent className="flex flex-col p-0">
				<Stepper className="p-16" activeStep={activeStepIndex} alternativeLabel>
					{steps.map((step, index) => (
						<Step key={index}>
							<StepLabel>{step.label}</StepLabel>
						</Step>
					))}
				</Stepper>
				{activeStep.content}
			</DialogContent>
			<DialogActions>
				{activeStepIndex > 0 ? (
					!isLastStep ? (
						<Button onClick={previousStep}>{t('DIALOG_ACTION_BACK')}</Button>
					) : (
						<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CLOSE')}</Button>
					)
				) : (
					<></>
				)}
				{!isLastStep && <Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>}
				{activeStep.handleNextStep && (
					<Button color="secondary" onClick={activeStep.handleNextStep} disabled={activeStep.disableNext}>
						{t('DIALOG_ACTION_NEXT')}
					</Button>
				)}
			</DialogActions>
		</>
	);
}
